import { BACKEND_ENDPOINT, BACKEND_PROXY_PATH } from '@/config/backendEndpoint';

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

/**
 * 브라우저에서 사용할 API 베이스 경로.
 * 개발환경은 동일 출처 프록시(`/api/backend`)를 그대로 사용한다.
 */
export function getBrowserApiEndpoint() {
  return (
    stripTrailingSlash(process.env.NEXT_PUBLIC_API_ENDPOINT ?? '') ||
    BACKEND_PROXY_PATH
  );
}

/**
 * Node(SSR/ISR/빌드)에서 사용할 API 절대주소.
 * 프록시 경로(`/api/backend`)는 서버에서 해석할 수 없으므로
 * 업스트림 절대주소로 대체한다.
 */
export function getServerApiEndpoint() {
  if (process.env.K_SERVICE) {
    return `http://127.0.0.1:${process.env.PORT || '8080'}/api/backend`;
  }

  const upstream = stripTrailingSlash(process.env.API_UPSTREAM_ENDPOINT ?? '');
  if (isAbsoluteUrl(upstream)) {
    return upstream;
  }

  const browserEndpoint = getBrowserApiEndpoint();
  if (isAbsoluteUrl(browserEndpoint)) {
    return browserEndpoint;
  }

  return BACKEND_ENDPOINT;
}

export function getBrowserApiUrl(path: string) {
  const base = `${getBrowserApiEndpoint()}/`;
  return new URL(path, new URL(base, window.location.origin));
}

/**
 * 실행 환경에 맞는 API 베이스 URL
 * (브라우저: 동일 출처 프록시 / 서버: 업스트림 절대주소)
 */
export function getApiEndpoint() {
  return typeof window === 'undefined'
    ? getServerApiEndpoint()
    : getBrowserApiEndpoint();
}

/**
 * 실행 환경의 API 베이스에 경로를 붙여 요청 URL 을 만든다.
 *
 * `new URL(path, base)` 는 쓰지 않는다. 개발환경 베이스는 동일 출처 프록시
 * 경로(`/api/backend`)라 origin 이 없고, 상대 경로를 base 로 받으면
 * `new URL` 은 `Invalid base URL` 로 던진다. 문자열로 이어 붙여 상대 경로
 * 그대로 두면 fetch 가 문서 origin 기준으로 해석하고, 절대주소 베이스는
 * origin 을 그대로 유지한다.
 */
export function buildApiUrl(path: string) {
  return `${getApiEndpoint()}/${path.replace(/^\/+/, '')}`;
}
